'use client';

import { c as _c } from "react/compiler-runtime";
import type { Money, SellingPlan } from '@repo/shopify';
import Image from 'next/image';
import { useId } from 'react';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { formatMoney } from '@/lib/money';
import { getDefaultSellingPlan, getSellingPlanFrequencyOptionLabel, getSellingPlanIntervalDays, getSellingPlanPercentOff, getSellingPlanUnitPrice } from '@/lib/selling-plans';
import type { SubscribeSaveConfig } from '@/lib/subscribe-save-config';
interface PurchaseOptionSelectorProps {
  plans: SellingPlan[];
  /** Current (variant) one-time price the subscription discount is computed from. */
  price: Money;
  /** Selected plan id; null = one-time purchase. */
  selectedPlanId: string | null;
  onSelect: (planId: string | null) => void;
  /** Hide the one-time option for subscription-only products. */
  requiresSellingPlan?: boolean;
  /**
   * Product thumbnail inside the subscription plan row (mockup plan thumb).
   * Only rendered for single-plan products — repeating the same image on
   * every frequency row reads as noise.
   */
  thumbnail?: {
    url: string;
    alt: string;
  } | null;
  /** CMS-driven labels, gifts, and copy. */
  config: SubscribeSaveConfig;
  moneyLocale?: string;
}

/**
 * Whether a CMS gift value is money ("$34.99", "43.98") rather than text like
 * "Chance To Win" — only monetary values get the strikethrough treatment.
 */
function isMonetaryValue(value: string) {
  return /^[$€£]?\s*\d[\d.,]*$/.test(value.trim());
}

/** Recurring plans ordered shortest delivery interval first (unknowns last). */
function sortPlansByInterval(plans: SellingPlan[]): SellingPlan[] {
  return [...plans].sort((a, b) => (getSellingPlanIntervalDays(a) ?? Number.MAX_SAFE_INTEGER) - (getSellingPlanIntervalDays(b) ?? Number.MAX_SAFE_INTEGER));
}
function RadioDot(t0) {
  const $ = _c(5);
  const {
    checked
  } = t0;
  const t1 = `flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${checked ? "border-neutral-900" : "border-neutral-300"}`;
  let t2;
  if ($[0] !== checked) {
    t2 = checked && <span className="h-2 w-2 rounded-full bg-neutral-900" />;
    $[0] = checked;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <span aria-hidden="true" className={t1}>{t2}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * Subscription-first purchase card matched to the buy-box mockup: a bordered
 * card carrying a "Most Popular · Save X%" ribbon, one row per recurring plan
 * (product thumb on single-plan products, frequency + CMS plan meta,
 * struck-through one-time price, per-delivery price), the CMS-driven
 * free-gift list with a bundle-value summary, and the
 * one-time purchase demoted to a slim row underneath.
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: one card combines plan pricing, frequency selection, and gift rendering (same shape as ProductInfo)
export function PurchaseOptionSelector(t0) {
  const $ = _c(100);
  const {
    plans,
    price,
    selectedPlanId,
    onSelect,
    requiresSellingPlan: t1,
    thumbnail,
    config,
    moneyLocale
  } = t0;
  const requiresSellingPlan = t1 === undefined ? false : t1;
  const radioGroupName = useId();
  if (plans.length === 0) {
    return null;
  }
  let isSubscribeSelected;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== config || $[1] !== moneyLocale || $[2] !== onSelect || $[3] !== plans || $[4] !== price || $[5] !== radioGroupName || $[6] !== selectedPlanId || $[7] !== thumbnail) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedPlans = sortPlansByInterval(plans);
      let t9;
      if ($[16] !== plans || $[17] !== selectedPlanId) {
        let t10;
        if ($[19] !== selectedPlanId) {
          t10 = plan => plan.id === selectedPlanId;
          $[19] = selectedPlanId;
          $[20] = t10;
        } else {
          t10 = $[20];
        }
        t9 = plans.some(t10);
        $[16] = plans;
        $[17] = selectedPlanId;
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      isSubscribeSelected = t9;
      let ribbonPlan;
      let t10;
      let t11;
      let t12;
      let t13;
      let t14;
      let t15;
      let t16;
      if ($[21] !== config || $[22] !== isSubscribeSelected || $[23] !== moneyLocale || $[24] !== onSelect || $[25] !== plans || $[26] !== price || $[27] !== radioGroupName || $[28] !== selectedPlanId || $[29] !== thumbnail) {
        ribbonPlan = plans.find(plan_0 => plan_0.id === selectedPlanId) ?? getDefaultSellingPlan(plans);
        if (!ribbonPlan) {
          t8 = null;
          break bb0;
        }
        const planPrice = getSellingPlanUnitPrice(price, ribbonPlan);
        const percentOff = getSellingPlanPercentOff(price, ribbonPlan);
        const ribbonText = percentOff !== null ? `${config.popularBadge} · Save ${percentOff}%` : config.popularBadge;
        if ($[42] !== config.title) {
          t7 = <legend className="mb-3 text-[13px] font-bold uppercase tracking-wide text-neutral-900">{config.title}</legend>;
          $[42] = config.title;
          $[43] = t7;
        } else {
          t7 = $[43];
        }
        t2 = `relative cursor-pointer rounded border transition-colors ${isSubscribeSelected ? "border-neutral-900 ring-1 ring-neutral-900" : "border-neutral-300 hover:border-neutral-400"}`;
        t3 = event => {
          if (event.target instanceof Element && event.target.closest("label")) {
            return;
          }
          onSelect(ribbonPlan.id);
        };
        const t17 = `absolute -top-[11px] left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-1 text-[10px] font-bold uppercase leading-none tracking-[0.06em] ${isSubscribeSelected ? "bg-brand-accent text-white" : "bg-neutral-200 text-neutral-600"}`;
        if ($[44] !== ribbonText || $[45] !== t17) {
          t4 = <span className={t17}>{ribbonText}</span>;
          $[44] = ribbonText;
          $[45] = t17;
          $[46] = t4;
        } else {
          t4 = $[46];
        }
        t14 = "flex cursor-pointer items-center justify-between gap-3 px-3 pt-4 pb-2.5";
        t15 = <input type="radio" name={radioGroupName} className="sr-only" checked={isSubscribeSelected} onChange={() => onSelect(ribbonPlan.id)} />;
        let t18;
        if ($[47] !== isSubscribeSelected || $[48] !== thumbnail) {
          t18 = thumbnail ? <span className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-[3px] border border-neutral-200"><ShopifyImage src={thumbnail.url} alt={thumbnail.alt} fill={true} sizes="48px" className="object-cover" /></span> : <RadioDot checked={isSubscribeSelected} />;
          $[47] = isSubscribeSelected;
          $[48] = thumbnail;
          $[49] = t18;
        } else {
          t18 = $[49];
        }
        let t19;
        if ($[50] !== config.subscribeLabel) {
          t19 = <span className="block text-[13px] font-bold uppercase tracking-wide text-neutral-900">{config.subscribeLabel}</span>;
          $[50] = config.subscribeLabel;
          $[51] = t19;
        } else {
          t19 = $[51];
        }
        let t20;
        if ($[52] !== config.planMeta) {
          t20 = config.planMeta && <span className="block text-[11px] text-neutral-500">{config.planMeta}</span>;
          $[52] = config.planMeta;
          $[53] = t20;
        } else {
          t20 = $[53];
        }
        let t21;
        if ($[54] !== t19 || $[55] !== t20) {
          t21 = <span>{t19}{t20}</span>;
          $[54] = t19;
          $[55] = t20;
          $[56] = t21;
        } else {
          t21 = $[56];
        }
        if ($[57] !== t18 || $[58] !== t21) {
          t16 = <span className="flex items-center gap-2.5">{t18}{t21}</span>;
          $[57] = t18;
          $[58] = t21;
          $[59] = t16;
        } else {
          t16 = $[59];
        }
        t12 = "flex flex-col items-end";
        t13 = percentOff !== null && <span className="text-[11px] leading-tight text-neutral-400 line-through">{formatMoney(price, moneyLocale)}</span>;
        t10 = "text-base font-black leading-tight text-neutral-900";
        t11 = formatMoney(planPrice, moneyLocale);
        $[21] = config;
        $[22] = isSubscribeSelected;
        $[23] = moneyLocale;
        $[24] = onSelect;
        $[25] = plans;
        $[26] = price;
        $[27] = radioGroupName;
        $[28] = selectedPlanId;
        $[29] = thumbnail;
        $[30] = ribbonPlan;
        $[31] = t10;
        $[32] = t11;
        $[33] = t12;
        $[34] = t13;
        $[35] = t14;
        $[36] = t15;
        $[37] = t16;
        $[38] = t2;
        $[39] = t3;
        $[40] = t4;
        $[41] = t7;
      } else {
        ribbonPlan = $[30];
        t10 = $[31];
        t11 = $[32];
        t12 = $[33];
        t13 = $[34];
        t14 = $[35];
        t15 = $[36];
        t16 = $[37];
        t2 = $[38];
        t3 = $[39];
        t4 = $[40];
        t7 = $[41];
      }
      let t17;
      if ($[60] !== t10 || $[61] !== t11) {
        t17 = <span className={t10}>{t11}</span>;
        $[60] = t10;
        $[61] = t11;
        $[62] = t17;
      } else {
        t17 = $[62];
      }
      let t18;
      if ($[63] !== t12 || $[64] !== t13 || $[65] !== t17) {
        t18 = <span className={t12}>{t13}{t17}</span>;
        $[63] = t12;
        $[64] = t13;
        $[65] = t17;
        $[66] = t18;
      } else {
        t18 = $[66];
      }
      if ($[67] !== t14 || $[68] !== t15 || $[69] !== t16 || $[70] !== t18) {
        t5 = <label className={t14}>{t15}{t16}{t18}</label>;
        $[67] = t14;
        $[68] = t15;
        $[69] = t16;
        $[70] = t18;
        $[71] = t5;
      } else {
        t5 = $[71];
      }
      t6 = isSubscribeSelected && sortedPlans.length > 1 && <label className="flex cursor-pointer items-center justify-between gap-3 border-t border-neutral-200 px-3 py-1.5 text-[11px] leading-none text-neutral-500"><span className="flex-shrink-0 leading-none">Deliver every</span><select className="w-auto min-w-0 cursor-pointer appearance-none border-0 bg-transparent bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2210%22%20height%3D%2210%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23525252%22%20stroke-width%3D%222.5%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[position:right_0_center] bg-no-repeat p-0 pr-3.5 text-[11px] font-semibold leading-none text-neutral-800 underline decoration-neutral-300 underline-offset-2 transition-colors hover:decoration-neutral-800 focus:outline-none" value={ribbonPlan.id} onChange={event_0 => onSelect(event_0.target.value)}>{sortedPlans.map(_temp)}</select></label>;
    }
    $[0] = config;
    $[1] = moneyLocale;
    $[2] = onSelect;
    $[3] = plans;
    $[4] = price;
    $[5] = radioGroupName;
    $[6] = selectedPlanId;
    $[7] = thumbnail;
    $[8] = isSubscribeSelected;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    isSubscribeSelected = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[72] !== config.giftBundleValue || $[73] !== config.gifts.length || $[74] !== isSubscribeSelected) {
    t9 = config.gifts.length > 0 && !isSubscribeSelected && <div className="flex items-center justify-between gap-2.5 rounded-b-[3px] border-t border-neutral-200 bg-neutral-50 px-3 py-2"><span className="flex min-w-0 items-center gap-2"><span className="flex-shrink-0 rounded bg-brand-accent px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">{config.gifts.length} Free Gift{config.gifts.length === 1 ? "" : "s"}</span>{config.giftBundleValue && <span className="truncate text-[12px] text-neutral-700">{config.giftBundleValue} value, with your first order</span>}</span><span className="flex-shrink-0 text-[11px] font-bold uppercase tracking-wide text-brand-accent">See All</span></div>;
    $[72] = config.giftBundleValue;
    $[73] = config.gifts.length;
    $[74] = isSubscribeSelected;
    $[75] = t9;
  } else {
    t9 = $[75];
  }
  let t10;
  if ($[76] !== config.gifts || $[77] !== config.giftsHeading || $[78] !== isSubscribeSelected) {
    t10 = config.gifts.length > 0 && isSubscribeSelected && <div className="border-t border-neutral-200 px-3 py-2.5"><p className="text-[11px] font-bold uppercase tracking-wider text-brand-accent">{config.giftsHeading}</p><ul className="mt-2 space-y-2">{config.gifts.map(_temp2)}</ul></div>;
    $[76] = config.gifts;
    $[77] = config.giftsHeading;
    $[78] = isSubscribeSelected;
    $[79] = t10;
  } else {
    t10 = $[79];
  }
  let t11;
  if ($[80] !== t10 || $[81] !== t2 || $[82] !== t3 || $[83] !== t4 || $[84] !== t5 || $[85] !== t6 || $[86] !== t9) {
    t11 = <div className={t2} onClick={t3}>{t4}{t5}{t6}{t9}{t10}</div>;
    $[80] = t10;
    $[81] = t2;
    $[82] = t3;
    $[83] = t4;
    $[84] = t5;
    $[85] = t6;
    $[86] = t9;
    $[87] = t11;
  } else {
    t11 = $[87];
  }
  let t12;
  if ($[88] !== config.oneTimeLabel || $[89] !== isSubscribeSelected || $[90] !== moneyLocale || $[91] !== onSelect || $[92] !== price || $[93] !== radioGroupName || $[94] !== requiresSellingPlan) {
    t12 = !requiresSellingPlan && <label className={`mt-2 flex cursor-pointer items-center justify-between gap-3 rounded border px-3 py-2.5 transition-colors ${!isSubscribeSelected ? "border-neutral-900 ring-1 ring-neutral-900" : "border-neutral-300 hover:border-neutral-400"}`}><input type="radio" name={radioGroupName} className="sr-only" checked={!isSubscribeSelected} onChange={() => onSelect(null)} /><span className="text-[13px] font-medium text-neutral-700">{config.oneTimeLabel}</span><span className="text-[13px] font-bold text-neutral-900">{formatMoney(price, moneyLocale)}</span></label>;
    $[88] = config.oneTimeLabel;
    $[89] = isSubscribeSelected;
    $[90] = moneyLocale;
    $[91] = onSelect;
    $[92] = price;
    $[93] = radioGroupName;
    $[94] = requiresSellingPlan;
    $[95] = t12;
  } else {
    t12 = $[95];
  }
  let t13;
  if ($[96] !== t11 || $[97] !== t12 || $[98] !== t7) {
    t13 = <fieldset>{t7}{t11}{t12}</fieldset>;
    $[96] = t11;
    $[97] = t12;
    $[98] = t7;
    $[99] = t13;
  } else {
    t13 = $[99];
  }
  return t13;
}
function _temp2(gift) {
  return <li key={gift.title} className="flex items-center gap-2.5"><span className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-neutral-100">{gift.imageUrl && <Image src={gift.imageUrl} alt={gift.imageAlt} fill={true} sizes="40px" style={{
        objectFit: "cover"
      }} />}</span><span className="min-w-0 flex-1"><span className="block truncate text-[12px] font-bold uppercase tracking-wide text-neutral-900">{gift.title}</span>{gift.description && <span className="block truncate text-[11px] text-neutral-500">{gift.description}</span>}</span><span className="flex flex-shrink-0 items-center gap-1.5">{gift.value && <span className={`text-[11px] text-neutral-400 ${isMonetaryValue(gift.value) ? "line-through" : ""}`}>{gift.value}</span>}<span className="rounded bg-brand-accent px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Free</span></span></li>;
}
function _temp(plan_1) {
  return <option key={plan_1.id} value={plan_1.id}>{getSellingPlanFrequencyOptionLabel(plan_1)}</option>;
}